/* eslint-disable no-nested-ternary */
import classNames from 'classnames';
import { useMemo } from 'react';

import { getComponentFromName, getImageAtSize } from '../../lib/utils';

import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import * as IconComponents from '../icons/indicators';

import styles from '../../../styles/lists/questions-indicator.module.scss';

interface QuestionsIndicatorProps {
    currentIndex?: number | null;
    questions?: Question[] | null;
    answers?: ParticipationAnswer[] | null;
    iconComponent?: string | null;
    currentIconComponent: string | null;
    withProgress?: boolean;
    withImage?: boolean;
    withIndex?: boolean;
    className?: string | null;
}

function QuestionsIndicator({
    currentIndex = null,
    questions = null,
    answers = null,
    iconComponent: providedIconComponent = null,
    currentIconComponent: providedCurrentIconComponent = null,
    withProgress = false,
    withIndex = false,
    withImage = false,
    className = null,
}: QuestionsIndicatorProps) {
    const { name: theme } = useTheme();
    const themeClassName = useThemeClassName(styles);

    const iconComponent = useMemo(() => {
        if (providedIconComponent !== null) {
            return providedIconComponent;
        }
        if (theme === 'iga_noel') {
            return 'thumb';
        }
        if (theme === 'coorslive') {
            return 'coorslight-mountain';
        }
        return 'circle';
    }, [theme]);

    const currentIconComponent = useMemo(() => {
        if (providedCurrentIconComponent !== null) {
            return providedCurrentIconComponent;
        }
        if (theme === 'iga_noel') {
            return 'sparkle';
        }
        return null;
    }, [theme]);

    const IconComponent = getComponentFromName(IconComponents, iconComponent);
    const CurrentIconComponent = getComponentFromName(IconComponents, currentIconComponent);

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            {withProgress ? (
                <div className={styles.progress}>
                    <span>{currentIndex! + 1}</span> / <span>{questions!.length}</span>
                </div>
            ) : null}
            <ul className={styles.items}>
                {questions!.map(({ id, image = null }, index) => {
                    const userAnswer =
                        (answers || []).find(({ question }) => question === id) || null;
                    const { valid = null } = userAnswer || {};
                    const { url: imageUrl = null } =
                        (image !== null ? getImageAtSize(image, 'small') : null) || {};
                    const FinalIconComponent =
                        CurrentIconComponent !== null && index === currentIndex
                            ? CurrentIconComponent
                            : IconComponent;
                    return (
                        <li
                            key={`nav-item-${id}`}
                            className={classNames([
                                styles.item,
                                {
                                    [styles.current]: index === currentIndex,
                                    [styles.answered]: userAnswer !== null,
                                    [styles.invalid]: valid === false,
                                    [styles.valid]: valid === true,
                                    [styles.withIcon]: IconComponent !== null,
                                },
                            ])}
                        >
                            {imageUrl !== null && withImage ? (
                                <div className={styles.imageContainer}>
                                    <img className={styles.image} src={imageUrl} alt={index} />
                                </div>
                            ) : null}
                            {FinalIconComponent !== null ? (
                                <FinalIconComponent
                                    className={styles.icon}
                                    current={index === currentIndex}
                                    answered={valid !== null}
                                    valid={valid}
                                />
                            ) : null}
                            {withIndex ? (
                                <div className={styles.index}>
                                    <span>{index + 1}</span>
                                </div>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default QuestionsIndicator;
